/**
 * FileChunker — lazily slices a File into fixed-size chunks.
 *
 * Critically, it NEVER reads the whole file into memory: each chunk is produced
 * on demand via `Blob.slice()` + `.arrayBuffer()`. This keeps memory flat
 * regardless of file size (50GB+ works in well under 200MB).
 */
export class FileChunker {
  readonly totalChunks: number;

  constructor(
    private readonly file: Blob,
    readonly chunkSize: number,
  ) {
    if (chunkSize <= 0) throw new Error('chunkSize must be positive');
    this.totalChunks = file.size === 0 ? 0 : Math.ceil(file.size / chunkSize);
  }

  get size(): number {
    return this.file.size;
  }

  byteRange(index: number): { start: number; end: number } {
    const start = index * this.chunkSize;
    const end = Math.min(start + this.chunkSize, this.file.size);
    return { start, end };
  }

  /** Read a single chunk by index. */
  async getChunk(index: number): Promise<ArrayBuffer> {
    if (index < 0 || index >= this.totalChunks) {
      throw new RangeError(`chunk index ${index} out of range [0, ${this.totalChunks})`);
    }
    const { start, end } = this.byteRange(index);
    return this.file.slice(start, end).arrayBuffer();
  }

  /** Iterate chunks in order, optionally starting at `fromIndex`. */
  async *iterate(fromIndex = 0): AsyncGenerator<{ index: number; data: ArrayBuffer }> {
    for (let index = fromIndex; index < this.totalChunks; index++) {
      yield { index, data: await this.getChunk(index) };
    }
  }

  /** Iterate only the given chunk indices (used for resume retransmission). */
  async *iterateIndices(indices: Iterable<number>): AsyncGenerator<{ index: number; data: ArrayBuffer }> {
    for (const index of indices) {
      yield { index, data: await this.getChunk(index) };
    }
  }
}
